import { entities } from "sbtech-sports-data-api-client";
import { MasterEvent, LiveGameState, Scoreboard, MasterEventInitialData } from "../../data";
import { UIPreferences } from "..";
import { getCurrentTime } from "sb-resp-lib";
import { Scores, TeamScoreParser } from "../TeamScoreParser";
import { ScoreboardParser } from "../ScoreboardParser";

type TeamNames = {
    Home: string;
    Away: string;
};

export type FormattedDateTime = {
    Date: string;
    Time: string;
};

export class MasterEventParser {
    private static _instance: MasterEventParser;

    public static get instance(): MasterEventParser {
        if (!MasterEventParser._instance) {
            MasterEventParser._instance = new MasterEventParser();
        }
        return MasterEventParser._instance;
    }

    private constructor() { }

    public parse(sportEvent: entities.SportEvent, existingEvent: MasterEvent | undefined, uiPreferences: UIPreferences): MasterEvent {

        if (existingEvent) {
            return this.parseUpdate(sportEvent, existingEvent);
        }

        let initialParseEvent = this.parseInitial(sportEvent, uiPreferences);
        return this.parseUpdate(sportEvent, initialParseEvent);
    }

    private parseInitial(sportEvent: entities.SportEvent, uiPreferences: UIPreferences): MasterEventInitialData {

        let teamNames: TeamNames = this.parseTeamNames(sportEvent);
        let dateTime: FormattedDateTime = this.parseDateTime(sportEvent, uiPreferences);

        let masterEvent: MasterEventInitialData = new MasterEventInitialData({
            eventName: sportEvent.eventName ? sportEvent.eventName : "",
            sportName: sportEvent.sportName,
            sportID: sportEvent.sportId,
            id: sportEvent.id,
            team1Name: teamNames.Home,
            team2Name: teamNames.Away,
            leagueID: sportEvent.leagueId,
            leagueName: sportEvent.leagueName,
            eventDate: dateTime.Date,
            eventTime: dateTime.Time,
            isTeamSwapEnabled: sportEvent.isTeamSwap,
            eventDateTime: sportEvent.startEventDate,
            regionId: sportEvent.regionId,
        });

        return masterEvent;
    }

    private parseUpdate(sportEvent: entities.SportEvent, existingEvent: MasterEventInitialData): MasterEvent {

        let teamScores: Scores = TeamScoreParser.instance.parse(sportEvent, existingEvent.sportID);
        let liveGameState: LiveGameState | undefined = this.parseLiveGameState(sportEvent);
        let liveStartCountdown: number = this.parseLiveStartCountdown(existingEvent.eventDateTime);
        let scoreboard: Scoreboard | undefined = ScoreboardParser.instance.parse(sportEvent, existingEvent.sportID, existingEvent.isTeamSwapEnabled);

        let masterEvent = new MasterEvent(
            {...existingEvent,
                score1: teamScores.Home,
                score2: teamScores.Away,
                isLive: sportEvent.isLive,
                markets: [],
                liveGameState,
                liveStartCountdown,
                scoreboard,
                status: sportEvent.status}
        );

        return masterEvent;
    }

    private parseLiveGameState(sportEvent: entities.SportEvent): LiveGameState | undefined {
        if (!sportEvent.liveGameState) {
            return undefined;
        }

        return new LiveGameState({
            ...sportEvent.liveGameState,
            gameTime: sportEvent.liveGameState.gameTime ?
                sportEvent.liveGameState.gameTime * 1000 : 0
        });
    }

    public parseLiveStartCountdown(eventDateTime: string): number {
        let eventStartingDate = Date.fromISO(eventDateTime);
        if (!eventStartingDate) {
            return 0;
        }

        let delta: number = eventStartingDate.getTime() - (window["serverdate"] || new Date()).getTime();
        return (delta < 0) ? 0 : delta;
    }

    private parseTeamNames(sportEvent: entities.SportEvent): TeamNames {
        if (!sportEvent.participants || !sportEvent.participants[0] || !sportEvent.participants[1]) {
            return { Home: "", Away: "" };
        }

        if (sportEvent.participants[0].venueRole && sportEvent.participants[1].venueRole) {
            return this.parseTeamNamesByVenue(sportEvent);
        }

        return this.parseTeamNamesByIndex(sportEvent);
    }

    private parseTeamNamesByIndex(sportEvent: entities.SportEvent): TeamNames {
        return sportEvent.isTeamSwap
            ? { Home: sportEvent.participants[1].name, Away: sportEvent.participants[0].name }
            : { Home: sportEvent.participants[0].name, Away: sportEvent.participants[1].name };
    }

    private parseTeamNamesByVenue(sportEvent: entities.SportEvent) {
        let homeParticipant = sportEvent.participants.find((par) => par.venueRole === "Home");
        let awayParticipant = sportEvent.participants.find((par) => par.venueRole === "Away");
        return sportEvent.isTeamSwap
            ? { Home: awayParticipant ? awayParticipant.name : "", Away: homeParticipant ? homeParticipant.name : "" }
            : { Home: homeParticipant ? homeParticipant.name : "", Away: awayParticipant ? awayParticipant.name : "" };
    }

    private parseDateTime(sportEvent: entities.SportEvent, uiPreferences: UIPreferences): FormattedDateTime {
        let datetime: FormattedDateTime = this.getFormattedDateTime(sportEvent.startEventDate, uiPreferences.timeZone, uiPreferences.dateFormat, uiPreferences.timeFormat);

        return {
            Date: datetime.Date,
            Time: datetime.Time
        };
    }

    public getFormattedDateTime(date: string, timeZone: number, dateFormat: string, timeFormat: string): FormattedDateTime {
        let convertedDate = Date.fromISO(date);
        if (convertedDate === undefined) {
            throw "invalid argument";
        }

        let currentDate: Date = getCurrentTime(convertedDate, timeZone);
        let formattedDate: string = currentDate.toStringEx(dateFormat);
        let formattedTime: string = currentDate.formatDate(timeFormat);

        return {
            Date: formattedDate,
            Time: formattedTime
        };
    }

    private liveTimeCalculatorByClockDirection = {
        [entities.ClockDirection.Timer]: (gameTime: number, interval: number) => gameTime - interval,
        [entities.ClockDirection.Stopwatch]: (gameTime: number, interval: number) => gameTime + interval
    };

    public getLiveGameStateTime(liveGameState: LiveGameState, newInterval: number): number {
        if (!liveGameState || !liveGameState.gameTime) {
            return 0;
        }

        if (!liveGameState.clockRunning) {
            return liveGameState.gameTime;
        }

        return this.liveTimeCalculatorByClockDirection[liveGameState.clockDirection](liveGameState.gameTime, newInterval);
    }

    public getLiveStartCountdownTime(liveStartCountdown: number, interval: number): number {
        let newLiveStartCountdown: number = liveStartCountdown - interval;
        return newLiveStartCountdown < 0 ? 0 : newLiveStartCountdown;
    }
}